import { useEffect, useState } from 'react';
import { Viewers } from './Viewers';

/**
 * Top strip: London & New York session state, ticking every second.
 *  - OPEN       → countdown to close
 *  - closed     → countdown to the next valid open (weekends + holidays skipped)
 *  - holiday    → named ("CLOSED — Thanksgiving") + countdown to next open
 * Pure client-side clock math in each exchange's own timezone (DST-correct).
 * Full-day holidays only; half-days (e.g. Black Friday early close) not modeled.
 */

interface Exchange {
  key: string;
  label: string;
  flag: string;
  tz: string;
  openMins: number;
  closeMins: number;
  holidays: Record<string, string>; // 'YYYY-MM-DD' → name
}

const EXCHANGES: Exchange[] = [
  {
    key: 'LSE',
    label: 'LONDON',
    flag: '🇬🇧',
    tz: 'Europe/London',
    openMins: 8 * 60,
    closeMins: 16 * 60 + 30,
    holidays: {
      '2026-01-01': "New Year's Day",
      '2026-04-03': 'Good Friday',
      '2026-04-06': 'Easter Monday',
      '2026-05-04': 'Early May Bank Holiday',
      '2026-05-25': 'Spring Bank Holiday',
      '2026-08-31': 'Summer Bank Holiday',
      '2026-12-25': 'Christmas Day',
      '2026-12-28': 'Boxing Day (observed)',
      '2027-01-01': "New Year's Day",
      '2027-03-26': 'Good Friday',
      '2027-03-29': 'Easter Monday',
      '2027-05-03': 'Early May Bank Holiday',
      '2027-05-31': 'Spring Bank Holiday',
      '2027-08-30': 'Summer Bank Holiday',
      '2027-12-27': 'Christmas (observed)',
      '2027-12-28': 'Boxing Day (observed)',
    },
  },
  {
    key: 'NYSE',
    label: 'NEW YORK',
    flag: '🇺🇸',
    tz: 'America/New_York',
    openMins: 9 * 60 + 30,
    closeMins: 16 * 60,
    holidays: {
      '2026-01-01': "New Year's Day",
      '2026-01-19': 'MLK Day',
      '2026-02-16': "Washington's Birthday",
      '2026-04-03': 'Good Friday',
      '2026-05-25': 'Memorial Day',
      '2026-06-19': 'Juneteenth',
      '2026-07-03': 'Independence Day (observed)',
      '2026-09-07': 'Labor Day',
      '2026-11-26': 'Thanksgiving',
      '2026-12-25': 'Christmas Day',
      '2027-01-01': "New Year's Day",
      '2027-01-18': 'MLK Day',
      '2027-02-15': "Washington's Birthday",
      '2027-03-26': 'Good Friday',
      '2027-05-31': 'Memorial Day',
      '2027-06-18': 'Juneteenth (observed)',
      '2027-07-05': 'Independence Day (observed)',
      '2027-09-06': 'Labor Day',
      '2027-11-25': 'Thanksgiving',
      '2027-12-24': 'Christmas (observed)',
    },
  },
];

/** 'YYYY-MM-DD' for a timestamp in a timezone */
function tzDate(ts: number, tz: string): string {
  return new Intl.DateTimeFormat('en-CA', { timeZone: tz }).format(new Date(ts));
}

/** minutes since local midnight + weekday index for a timestamp in a timezone */
function tzClock(ts: number, tz: string): { mins: number; day: number } {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: tz,
    hour12: false,
    weekday: 'short',
    hour: '2-digit',
    minute: '2-digit',
  }).formatToParts(new Date(ts));
  const get = (t: string) => parts.find((p) => p.type === t)?.value || '';
  const days = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
  return {
    mins: (parseInt(get('hour'), 10) % 24) * 60 + parseInt(get('minute'), 10),
    day: days.indexOf(get('weekday')),
  };
}

/** UTC timestamp of local minutes-since-midnight on a local date in tz (DST-correct) */
function tzTimestamp(dateStr: string, minutes: number, tz: string): number {
  const noonUtc = new Date(`${dateStr}T12:00:00Z`).getTime();
  const { mins: localMinsAtNoonUtc } = tzClock(noonUtc, tz);
  const offsetMins = 12 * 60 - localMinsAtNoonUtc; // how far local lags UTC
  return new Date(`${dateStr}T00:00:00Z`).getTime() + (minutes + offsetMins) * 60000;
}

const isWeekend = (day: number) => day === 0 || day === 6;

interface SessionState {
  status: 'open' | 'closed' | 'holiday';
  holidayName: string | null;
  countdownMs: number; // to close when open, to next open otherwise
}

function sessionState(ex: Exchange, now: number): SessionState {
  const today = tzDate(now, ex.tz);
  const { mins, day } = tzClock(now, ex.tz);
  const todayHoliday = ex.holidays[today] ?? null;
  const tradingToday = !isWeekend(day) && !todayHoliday;

  if (tradingToday && mins >= ex.openMins && mins < ex.closeMins) {
    return {
      status: 'open',
      holidayName: null,
      countdownMs: tzTimestamp(today, ex.closeMins, ex.tz) - now,
    };
  }

  // Find the next valid open, skipping weekends and holidays
  for (let k = 0; k < 15; k++) {
    const ts = now + k * 86400000;
    const dateStr = tzDate(ts, ex.tz);
    const day2 = tzClock(tzTimestamp(dateStr, 12 * 60, ex.tz), ex.tz).day;
    if (isWeekend(day2) || ex.holidays[dateStr]) continue;
    const openTs = tzTimestamp(dateStr, ex.openMins, ex.tz);
    if (openTs <= now) continue; // today's open already passed
    return {
      status: todayHoliday ? 'holiday' : 'closed',
      holidayName: todayHoliday,
      countdownMs: openTs - now,
    };
  }
  return { status: 'closed', holidayName: todayHoliday, countdownMs: 0 };
}

function fmt(ms: number): string {
  const total = Math.max(0, Math.floor(ms / 1000));
  const d = Math.floor(total / 86400);
  const h = Math.floor((total % 86400) / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = total % 60;
  const two = (n: number) => String(n).padStart(2, '0');
  return `${d > 0 ? `${d}d ` : ''}${two(h)}:${two(m)}:${two(s)}`;
}

export function SessionBar() {
  const [now, setNow] = useState(() => Date.now());

  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, []);

  return (
    <div className="session-bar">
      {EXCHANGES.map((ex) => {
        const s = sessionState(ex, now);
        return (
          <span className="session-item" key={ex.key}>
            <span className="session-flag">{ex.flag}</span>
            <span className="session-name">{ex.label}</span>
            {s.status === 'open' ? (
              <>
                <span className="session-open">OPEN</span>
                <span className="session-dim">closes</span>
                <span className="session-count">{fmt(s.countdownMs)}</span>
              </>
            ) : (
              <>
                {s.status === 'holiday' && (
                  <span className="session-holiday">CLOSED — {s.holidayName}</span>
                )}
                <span className="session-dim">opens in</span>
                <span className="session-count">{fmt(s.countdownMs)}</span>
              </>
            )}
          </span>
        );
      })}
      <Viewers />
    </div>
  );
}
